import {
    EUROPACE_COMPLETENESS_STATUS,
    EUROPACE_FEASIBILITY_STATUS,
    EUROPACE_INSURED_RISKS,
    EUROPACE_PAYMENT_DATES,
    EUROPACE_SHOPWINDOW_CONDITION_USAGES,
    SALARY_CALCULATION_OPTIONS,
} from 'cpool-constants';
import {
    schemaConditions,
    schemaEuropaceOffer,
    schemaId,
    schemaLoanRequest,
    schemaVorgangsnummer,
} from 'cpool-schemas';
import { z } from 'zod';

import { censorLoanRequest, censorLoanRequestApplicant2Only } from '../../../../../utils/censor.utils';
import type { EventSchema } from '../../../../../utils/EventSchema';
import { _Event } from '../../../../_Event';
import { LoanRequest_Created } from '../../../LoanRequest_Created';

export class LoanRequest_EuropaceLoanOffersSuccessResponseReceived extends _Event {
    constructor(payload: EventSchema<LoanRequest_EuropaceLoanOffersSuccessResponseReceived>, deprecated = false) {
        super(payload, deprecated);
    }

    getSchema() {
        return z.object({
            conditions: schemaConditions,
            id: schemaId,
            incompletenessMessages: z.record(z.string(), z.number()),
            loanRequest: schemaLoanRequest,
            offers: z.array(
                // the offerId represents the id from Europace while the id represents the id from our system
                schemaEuropaceOffer.omit({ id: true }).and(z.object({ id: schemaId, offerId: z.string() })),
            ),
            response: z.unknown(), // the complete response from europace
            userId: schemaId, // der Adviser der die Anfrage stellt
            vorgangsnummer: schemaVorgangsnummer,
        });
    }

    getCensoredSchema() {
        return this.getSchema().transform((val) => {
            const transformedSchema = val;
            transformedSchema.loanRequest = censorLoanRequest(val.loanRequest);
            return transformedSchema;
        });
    }

    getCensoredSchemaApplicant2Only(_applicant2Id: string) {
        return this.getSchema().transform((val) => {
            const transformedSchema = val;
            transformedSchema.loanRequest = censorLoanRequestApplicant2Only(val.loanRequest);
            return transformedSchema;
        });
    }

    static example() {
        return {
            conditions: {
                commissionRequestInPercent: 2,
                dataSnippets: {
                    applicant1: {
                        excludeApplicant: false,
                        rentFree: false,
                        salary: 1000,
                        salaryMode: SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
                        withoutMaintenanceIncome: false,
                        withoutOtherIncome: false,
                        withoutRentIncome: false,
                        withoutSidejob: false,
                    },
                    applicant2: {
                        excludeApplicant: false,
                        rentFree: false,
                        salary: 1000,
                        salaryMode: SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
                        withoutMaintenanceIncome: false,
                        withoutOtherIncome: false,
                        withoutRentIncome: false,
                        withoutSidejob: false,
                    },
                },
                financingPurpose: EUROPACE_SHOPWINDOW_CONDITION_USAGES.FAHRZEUGKAUF,
                paymentDate: EUROPACE_PAYMENT_DATES.MONATSENDE,
                requestAmount: 100000,
                residualDebtInsurance: {
                    applicant1: {
                        death: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        unemployment: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        workIncapacity: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                    },
                    applicant2: {
                        death: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        unemployment: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        workIncapacity: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                    },
                },
                runtimeInMonths: 120,
                totalFinancedAmount: 100000,
            },
            id: '22097497-e7b6-402a-a65b-6c02c049d030',
            incompletenessMessages: {},
            loanRequest: LoanRequest_Created.exampleCensored(),
            offers: [
                {
                    anpassungen: {
                        angepasst: false,
                    },
                    gesamtkonditionen: {
                        effektivzins: 100,
                        gesamtbetrag: 200,
                        konditionsspanne: {
                            maximum: {
                                effektivzins: 100,
                                gesamtkreditbetrag: 200,
                                rateMonatlich: 400,
                                sollzins: 300,
                            },
                            minimum: {
                                effektivzins: 100,
                                gesamtkreditbetrag: 200,
                                rateMonatlich: 400,
                                sollzins: 300,
                            },
                        },
                        laufzeitInMonaten: 120,
                        nettokreditbetrag: 200,
                        rateMonatlich: 400,
                        sollzins: 300,
                    },
                    id: '22097497-e7b6-402a-a65b-6c02c049d030',
                    machbarkeit: {
                        messages: [],
                        status: EUROPACE_FEASIBILITY_STATUS.MACHBAR,
                    },
                    offerId:
                        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0ZXN0Ijp0cnVlfQ.cAyyocTRtFnHuVdCAUb5t2Z8fYBSQkotg72u7KTA9tQ',
                    ratenkredit: {
                        produktanbieter: {
                            anschrift: {
                                hausnummer: '123',
                                ort: 'Berlin',
                                plz: '12345',
                                strasse: 'Musterstraße',
                            },
                            logo: {
                                svg: 'svg',
                            },
                            name: 'kreditLoan',
                        },
                        produktbezeichnung: 'produktbezeichnung',
                        schlussrate: 123,
                    },
                    ratenschutz: {
                        praemieMonatlich: 123,
                        versicherteRisikenAntragsteller1: [EUROPACE_INSURED_RISKS.LEBEN],
                    },
                    sofortkredit: false,
                    vollstaendigkeit: {
                        messages: [],
                        status: EUROPACE_COMPLETENESS_STATUS.VOLLSTAENDIG,
                    },
                },
            ] as const,
            response: {},
            userId: '22097497-e7b6-402a-a65b-6c02c049d067',
            vorgangsnummer: 'KR3D1T',
        } satisfies EventSchema<LoanRequest_EuropaceLoanOffersSuccessResponseReceived>;
    }

    static exampleCensoredApplicant2Only() {
        const example = LoanRequest_EuropaceLoanOffersSuccessResponseReceived.example();
        example.loanRequest = censorLoanRequestApplicant2Only(example.loanRequest);
        return example;
    }

    static exampleCensored() {
        const censoredExample = LoanRequest_EuropaceLoanOffersSuccessResponseReceived.example();
        censoredExample.loanRequest = LoanRequest_Created.exampleCensored();
        return censoredExample satisfies z.infer<
            ReturnType<LoanRequest_EuropaceLoanOffersSuccessResponseReceived['getCensoredSchema']>
        >;
    }
}

export type Schema_LoanRequest_EuropaceLoanOffersSuccessResponseReceived =
    EventSchema<LoanRequest_EuropaceLoanOffersSuccessResponseReceived>;
